import { deleteOne, get, post, put } from "@/modules/request";
import {
  GenderEnum,
  MemberSubscriptionStatus,
  MemberType,
  PaymentMethod,
} from "@/enums/mateEnums";
import {
  PageRequest,
  PageResponse,
  Response,
  WriteResponse,
} from "@/types/api-types";
import {
  MateModuleTemplate,
  MemberPlan,
  MemberSubscription,
  ModuleEntityType,
  PowerCube,
  SelectEntityType,
  TimelineEvent,
  User,
  Worldview,
  WorldviewLocation,
} from "@/types/entity";
import { PowerCubeTransactionCategory, TransactionType } from "@/types/user";

export const userInitialize = async (params: {
  gender: GenderEnum;
}): Promise<WriteResponse<User>> => {
  return await post("/UserInitialize", params);
};

export const userCompleteSignup = async (params: {
  name?: string;
  email: string;
  token: number;
  mediaId?: number;
  inviteCode?: string;
}): Promise<WriteResponse<User>> => {
  return await post("/UserCompleteSignup", params);
};

export const redeemDiscordGift = async (code: string) => {
  return await post("/DiscordGift/Redeem", { code });
};

export const createOneWorldview = async (params: {
  name: string;
  description: string;
}): Promise<WriteResponse<Worldview>> => {
  return await post("/Worldview", params);
};

export const updateOneWorldview = async (
  id: number,
  params: Partial<Worldview>
): Promise<WriteResponse<Worldview>> => {
  return await put(`/Worldview/${id}`, params);
};

export const deleteOneWorldview = async (
  id: number
): Promise<WriteResponse<null>> => {
  return await deleteOne(`/Worldview/${id}`);
};

export const readWorldviewExplorePage = async (
  { page = 1, perPage = 999 } = {},
  { field = "dtUpdate", order = "desc" } = {}
): Promise<PageResponse<Worldview>> => {
  return await get("/WorldviewExplorePage", {
    pagination: { page, perPage },
    sort: { field, order },
  });
};

export const readWorldviewPage = async (
  { page = 1, perPage = 999 } = {},
  { field = "dtUpdate", order = "desc" } = {}
): Promise<PageResponse<Worldview>> => {
  return await get("/WorldviewPage", {
    pagination: { page, perPage },
    sort: { field, order },
  });
};

export const readWorldviewByUuid = async (
  uuid: string
): Promise<WriteResponse<Worldview>> => {
  return await get(`/Worldview/${uuid}`);
};

export const createOneWorldviewLocation = (
  request: Partial<WorldviewLocation>
): Promise<WriteResponse<WorldviewLocation>> =>
  post("/WorldviewLocation", request);

export const updateOneWorldviewLocation = (
  id: number,
  request: Partial<WorldviewLocation>
): Promise<WriteResponse<WorldviewLocation>> =>
  put(`/WorldviewLocation/${id}`, request);

export const deleteOneWorldviewLocation = (
  id: number
): Promise<WriteResponse<WorldviewLocation>> =>
  deleteOne(`/WorldviewLocation/${id}`);

// User Modulee
export const createOneMateModuleTemplate = async (
  mateModuleId: number,
  name: string
): Promise<WriteResponse<MateModuleTemplate>> => {
  return await post("/MateModuleTemplate", {
    mateModuleId,
    name,
  });
};

export const deleteOneMateModuleTemplate = async (
  id: number
): Promise<WriteResponse<null>> => {
  return await deleteOne(`/MateModuleTemplate/${id}`);
};

export const updateOneMateModuleTemplate = async (
  id: number,
  name: string
): Promise<WriteResponse<MateModuleTemplate>> => {
  return await put(`/MateModuleTemplate/${id}`, { name });
};

export const readMateModuleTemplatePage = async (
  {
    entityUuid,
    entityType,
  }: { entityUuid: string; entityType: ModuleEntityType },
  { page = 1, perPage = 999 } = {},
  { field = "dtUpdate", order = "desc" } = {}
): Promise<PageResponse<MateModuleTemplate>> => {
  return await get("/MateModuleTemplatePage", {
    filter: { entityUuid, entityType },
    pagination: { page, perPage },
    sort: { field, order },
  });
};
export const importMateModuleTemplate = async (
  entityId: number,
  entityType: ModuleEntityType,
  templateId: number
): Promise<WriteResponse<MateModuleTemplate>> => {
  return await post(`/MateModuleImport`, {
    entityId,
    entityType,
    templateId,
  });
};

// Timeline
export const createOneTimelineEvent = async (
  params: Partial<TimelineEvent>
): Promise<WriteResponse<TimelineEvent>> => {
  return await post("/TimelineEvent", params);
};

export const updateOneTimelineEvent = async (
  id: number,
  params: Partial<TimelineEvent>
): Promise<WriteResponse<TimelineEvent>> => {
  return await put(`/TimelineEvent/${id}`, params);
};

export const updateManyTimelineEvent = async (
  itemzz: Partial<TimelineEvent>[]
): Promise<WriteResponse<TimelineEvent[]>> => {
  return await post("/TimelineEvent/UpdateMany", { itemzz });
};

export const deleteOneTimelineEvent = async (
  id: number
): Promise<WriteResponse<null>> => {
  return await deleteOne(`/TimelineEvent/${id}`);
};

export const readTimelineEventPage = async (
  {
    entityId,
    entityType,
    universeId,
  }: { entityId?: number; entityType: ModuleEntityType; universeId: number },
  { page = 1, perPage = 999 } = {},
  { field = "order", order = "asc" } = {}
): Promise<PageResponse<TimelineEvent>> => {
  return await get("/TimelineEventPage", {
    filter: { entityId, entityType, universeId },
    pagination: { page, perPage },
    sort: { field, order },
  });
};

export const syncMomentToTimelineEvent = async (params: {
  mateId: number;
  momentIds: number[];
  targetTimelines: string[];
}): Promise<WriteResponse<void>> => {
  return await post("/TimelineEvent/SyncMoment", params);
};

export const createOneTimelineEventItem = async (params: {
  eventId: number;
  entityType: SelectEntityType;
  entityId: number;
}): Promise<WriteResponse<void>> => {
  return await post("/TimelineEventItem", params);
};

export const deleteOneTimelineEventItem = async (
  id: number
): Promise<WriteResponse<null>> => {
  return await deleteOne(`/TimelineEventItem/${id}`);
};

export const readMemberPlanPage = async (
  memberType?: MemberType
): Promise<PageResponse<MemberPlan>> => {
  return await get("/MemberPlanPage", {
    filter: { memberType },
    sort: { field: "id", order: "asc" },
  });
};

export const createOneMemberSubscription = async (params: {
  planId: number;
  paymentMethod: PaymentMethod;
}): Promise<{
  data: {
    item: MemberSubscription;
    paymentConfig: {
      appConfig: {
        appid: string;
        noncestr: string;
        package: string;
        partnerid: string;
        prepayid: string;
        sign: string;
        timestamp: number;
      };
      codeUrl?: string;
      h5Url?: string;
      prepayid: string;
    };
  };
}> => {
  return await post("/MemberSubscription", params);
};

export const readMemberSubscriptionPage = async (
  status = MemberSubscriptionStatus.Active
): Promise<PageResponse<MemberSubscription>> => {
  return await get("/MemberSubscriptionPage", {
    filter: { status },
    sort: { field: "id", order: "desc" },
  });
};

export const cancelOneMemberSubscription = async (
  id: number
): Promise<WriteResponse<MemberSubscription>> => {
  return await post(`/MemberSubscriptionCancel/${id}`);
};

export const resumeOneMemberSubscription = async (
  id: number
): Promise<{
  data: {
    item: MemberSubscription;
    paymentConfig?: {
      preEntrustwebId: string;
    };
  };
}> => {
  return await post(`/MemberSubscriptionRestore/${id}`);
};

// post /MemberSubscriptionRenew
export const renewOneMemberSubscription = async (
  id: number,
  data: {
    planId: number;
    paymentMethod: PaymentMethod;
  }
): Promise<Response<WriteResponse<MemberSubscription>>> => {
  return await post(`/MemberSubscriptionRenew/${id}`, data);
};

export const readPowerCubePage = async (
  params: PageRequest<{
    type?: TransactionType;
    category?: PowerCubeTransactionCategory;
    excludeZero?: number;
  }>
): Promise<PageResponse<PowerCube>> => {
  return await get("/PowerCubePage", params);
};
